import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Star, Award, Globe, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import OptimizedImage from '../components/OptimizedImage';

const TalentBooking = () => {
  const pastProjects = [
    {
      id: 1,
      title: "Bollywood Superstar Concert",
      category: "Live Entertainment",
      description: "Managed end-to-end logistics for a major Bollywood celebrity concert with 15,000 attendees. Coordinated artist travel, accommodation, technical requirements, and promotional activities.",
      talent: "A-List Bollywood Actor",
      eventType: "Live Concert",
      date: "March 2024"
    },
    {
      id: 2,
      title: "International DJ Festival",
      category: "Music Festival",
      description: "Booked and coordinated 10 international DJs for a 2-day electronic music festival. Managed artist contracts, technical riders, and VIP hospitality.",
      talent: "Multiple International DJs",
      eventType: "Music Festival",
      date: "February 2024"
    },
    {
      id: 3,
      title: "Celebrity Brand Ambassador Campaign",
      category: "Brand Activation",
      description: "Secured exclusive brand ambassador deal for luxury automotive brand. Coordinated photoshoots, promotional videos, and public appearances.",
      talent: "International Celebrity",
      eventType: "Brand Campaign",
      date: "January 2024"
    },
    {
      id: 4,
      title: "Fashion Show with Celebrity Models",
      category: "Fashion Event",
      description: "Booked international supermodels and celebrity influencers for Bangkok Fashion Week. Managed all talent requirements and media coordination.",
      talent: "5 International Models",
      eventType: "Fashion Show",
      date: "December 2023"
    },
    {
      id: 5,
      title: "Corporate Event Keynote Speaker",
      category: "Corporate Event",
      description: "Arranged renowned motivational speaker for annual corporate summit. Coordinated travel, speaking requirements, and VIP meet-and-greet sessions.",
      talent: "International Keynote Speaker",
      eventType: "Conference",
      date: "November 2023"
    },
    {
      id: 6,
      title: "Film Star Product Launch",
      category: "Product Launch",
      description: "Booked popular film actor for exclusive product launch. Managed media appearances, autograph sessions, and social media activations.",
      talent: "Leading Film Actor",
      eventType: "Product Launch",
      date: "October 2023"
    }
  ];

  const services = [
    "Celebrity & Influencer Management",
    "National & International Artists",
    "Artist Liaison & Coordination",
    "Private & Corporate Event Talent",
    "Entertainment Curation",
    "Contract Negotiation",
    "Tour Management",
    "Meet & Greet Coordination",
    "Promotional Appearances",
    "Social Media Activations",
    "VIP Hospitality Services",
    "Technical Rider Management"
  ];

  const talentCategories = [
    {
      title: "Bollywood & Film Stars",
      description: "Access to A-list actors and actresses for events and campaigns",
      icon: Star
    },
    {
      title: "International Artists",
      description: "Global musicians, DJs, and performers from around the world",
      icon: Globe
    },
    {
      title: "Social Media Influencers",
      description: "Top-tier influencers with millions of engaged followers",
      icon: TrendingUp
    },
    {
      title: "Sports Personalities",
      description: "Renowned athletes and sports celebrities for brand endorsements",
      icon: Award
    }
  ];

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/talent.png"
            alt="Talent Booking"
            className="w-full h-full object-cover"
            priority={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <p className="text-gold text-sm tracking-widest uppercase mb-4">Premium Service</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
            <span className="gold-gradient-text">Talent</span>
            <span className="gold-gradient-text block mt-2">Booking</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto mt-6">
            Connecting you with the world's most sought-after celebrities, artists, and influencers. We bring star power to your events with seamless talent management and coordination.
          </p>
          <div className="flex justify-center mt-8">
            <div className="relative w-96 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent shadow-lg">
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gold rounded-full shadow-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Talent Categories */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center mb-16 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Our Network</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Access to Top
              <span className="gold-gradient-text"> Talent</span>
            </h2>
            <p className="text-gray-300 text-lg">
              We have exclusive partnerships with celebrities and artists across multiple industries.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            {talentCategories.map((category, index) => (
              <Card key={index} className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                <div className="p-8 space-y-4">
                  <div className="bg-gold/10 w-16 h-16 rounded-sm flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <category.icon className="text-gold" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-silver group-hover:text-gold transition-colors">
                    {category.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {category.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          <div className="max-w-6xl mx-auto">
            <h3 className="text-xl font-semibold text-silver mb-6">What We Offer:</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="flex items-start gap-3 group">
                  <CheckCircle2 className="text-gold flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" size={20} />
                  <span className="text-gray-300">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Past Projects */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Success Stories</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Past
              <span className="gold-gradient-text"> Projects</span>
            </h2>
            <p className="text-gray-300 text-lg">
              A selection of successful talent bookings that showcase our expertise and industry connections.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pastProjects.map((project) => (
              <Card key={project.id} className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                <div className="p-8 space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gold text-xs tracking-wider uppercase">{project.category}</span>
                      <span className="text-gray-400 text-xs">{project.date}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-silver group-hover:text-gold transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-gold/10">
                    <div className="flex items-center gap-2 text-sm">
                      <Star className="text-gold" size={16} />
                      <span className="text-gray-400">Talent:</span>
                      <span className="text-silver font-semibold">{project.talent}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Award className="text-gold" size={16} />
                      <span className="text-gray-400">Type:</span>
                      <span className="text-silver">{project.eventType}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TalentBooking;

